/**
 * The blocked overview's left panel (platform design §6.4; Figma `175:5249`), in the slot
 * `FailuresPreviewCard` fills when the campaign is not blocked: the agenda read's own
 * `first_missed_slot`, the same route `AgendaPreviewCard` already fetches, never a second route.
 *
 * Renders nothing -- not a dash -- while the read is loading, on a failed read, and when this
 * campaign has nothing unfunded (`first_missed_slot: null`): a panel about a stopped schedule that
 * itself shows a placeholder would say the schedule stopped a second, contradictory way.
 */

import type { UseQueryResult } from '@tanstack/react-query';
import type { ReactNode } from 'react';

import { Link } from '@tanstack/react-router';
import { Coins } from 'lucide-react';

import { getLocale } from '../../../i18n';
import type { ApiFailureError } from '../../../lib/http/errors';
import { Button } from '../../../ui/button';
import { Card, CardContent, CardHeader } from '../../../ui/card';
import { Chip } from '../../../ui/chip';
import { NoOpenRequest } from '../../tenant/capacity/NoOpenRequest';
import { tt } from '../../tenant/i18n';
import type { Agenda } from './api/schemas';
import { dayLabel, formatTime } from './dayHeading';
import { cm } from './messages';

export function MissedSlotCard(props: {
  tenantId: string;
  /** The Figma frame names the organisation, not the campaign, the way the shell's own
   *  breadcrumb does: passed in rather than read here, so this card carries no session
   *  dependency of its own. */
  organisationName: string;
  query: UseQueryResult<Agenda, ApiFailureError>;
  now?: Date;
}): ReactNode {
  const { data } = props.query;
  if (data === undefined || data.first_missed_slot === null) return null;

  const now = props.now ?? new Date();
  const locale = getLocale();
  const missedAt = new Date(data.first_missed_slot);
  // The frame sets the day word mid-sentence and in the chip alike, so never capitalised.
  const day = dayLabel(missedAt, now).toLocaleLowerCase(locale);

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <h2 className="text-title-sm">{cm('missedSlot.title')}</h2>
        <Chip>{day}</Chip>
      </CardHeader>
      <CardContent>
        <div className="flex items-start gap-3 py-2" data-testid="missed-slot-row">
          <Coins aria-hidden className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
          <div className="flex-1">
            <p className="text-body-sm font-medium">
              {cm('failures.page.rowTitle', {
                account: props.organisationName,
                time: `${day}, ${formatTime(missedAt, locale)}`,
              })}
            </p>
            <p className="text-caption text-muted-foreground">{cm('missedSlot.reason')}</p>
          </div>
          <NoOpenRequest tenantId={props.tenantId} kind="CREDITS">
            <Button asChild variant="outline" size="sm">
              <Link to="/t/$tenantId/creditos" params={{ tenantId: props.tenantId }}>
                {tt('balance.zero.action')}
              </Link>
            </Button>
          </NoOpenRequest>
        </div>
      </CardContent>
    </Card>
  );
}
